import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { ModelsState, ModelType, ModelSettings, ModelConfiguration, ModelOption } from '../types';
import { GET_ALL_MODELS, UPDATE_MODEL } from '../../../../graphql/queries';
import { useNotificationService } from '../../../Notification';
import { useModelsTranslation } from '../../../../modules/i18n/hooks/useTranslation';

export const useModels = () => {
  const { showSuccess, showError } = useNotificationService();
  const { t } = useModelsTranslation();

  const [modelsState, setModelsState] = useState<ModelsState>({
    embedding: {
      type: 'embedding',
      currentSettings: {
        modelId: '',
        modelName: '',
        provider: '',
        isActive: false
      },
      availableModels: []
    },
    ranking: {
      type: 'ranking',
      currentSettings: {
        modelId: '',
        modelName: '',
        provider: '',
        isActive: false
      },
      availableModels: []
    },
    llm: {
      type: 'llm',
      currentSettings: {
        modelId: '',
        modelName: '',
        provider: '',
        isActive: false
      },
      availableModels: []
    },
    loading: false,
    error: null
  });

  const [selectedModelType, setSelectedModelType] = useState<ModelType | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  // Fetch all models from GraphQL
  const { data, loading, error, refetch } = useQuery(GET_ALL_MODELS);

  // Update model mutation
  const [updateModelMutation] = useMutation(UPDATE_MODEL);

  // Process GraphQL data into ModelsState format
  useEffect(() => {
    if (data?.findManyModels) {
      const models = data.findManyModels;

      // Separate models by type
      const embeddingModels = models.filter((m: any) => m.modelType === 'EMBEDDING');
      const rerankingModels = models.filter((m: any) => m.modelType === 'RERANKING');
      const llmModels = models.filter((m: any) => m.modelType === 'LLM');

      // Helper to create ModelConfiguration
      const createConfig = (type: ModelType, modelsList: any[]): ModelConfiguration => {
        const defaultModel = modelsList.find(m => m.isDefault) || modelsList[0];
        const fallbackModel = defaultModel?.fallbackModel;

        const fallbackSettings = fallbackModel ? {
          modelId: fallbackModel.code,
          modelName: fallbackModel.name,
          provider: fallbackModel.provider,
          isActive: true,
          ...fallbackModel.parameters
        } : null;

        return {
          type,
          currentSettings: defaultModel ? {
            modelId: defaultModel.code,
            modelName: defaultModel.name,
            provider: defaultModel.provider,
            isActive: defaultModel.isActive,
            fallbackModelId: defaultModel.fallbackModelId,
            fallbackModelName: fallbackModel?.name,
            fallbackProvider: fallbackModel?.provider,
            ...defaultModel.parameters
          } : {
            modelId: '',
            modelName: '',
            provider: '',
            isActive: false
          },
          fallbackSettings,
          availableModels: modelsList.map((m: any): ModelOption => ({
            id: m.code,
            name: m.name,
            provider: m.provider,
            description: m.description || ''
          }))
        };
      };

      setModelsState({
        embedding: createConfig('embedding', embeddingModels),
        ranking: createConfig('ranking', rerankingModels),
        llm: createConfig('llm', llmModels),
        loading: false,
        error: null
      });
    }
  }, [data]);

  // Handle loading and error states
  useEffect(() => {
    setModelsState(prev => ({
      ...prev,
      loading,
      error: error ? error.message : null
    }));
  }, [loading, error]);

  const openSettings = (modelType: ModelType) => {
    setSelectedModelType(modelType);
    setShowSettingsModal(true);
  };

  const closeSettings = () => {
    setSelectedModelType(null);
    setShowSettingsModal(false);
  };

  const saveModelSettings = async (modelType: ModelType, settings: ModelSettings) => {
    try {
      // Clear any previous errors
      setModelsState(prev => ({
        ...prev,
        error: null
      }));

      // Find the model by code in the current type
      const modelToUpdate = data?.findManyModels?.find(
        (m: any) => m.code === settings.modelId && m.modelType === modelType.toUpperCase()
      );

      if (!modelToUpdate) {
        throw new Error('Model not found');
      }

      // Extract parameters based on model type
      const { modelId: _modelId, modelName: _modelName, provider: _provider, isActive, ...parameters } = settings;

      // Update via GraphQL mutation
      await updateModelMutation({
        variables: {
          id: modelToUpdate.id,
          data: {
            isActive,
            parameters
          }
        }
      });

      // Refetch to get updated data
      await refetch();

      // Update local state
      setModelsState(prev => ({
        ...prev,
        [modelType]: {
          ...prev[modelType],
          currentSettings: settings
        }
      }));

      closeSettings();
      showSuccess(t('notifications.saveSuccess'), t('notifications.saveSuccessMessage'));
    } catch (error: any) {
      const msg = error.message || t('notifications.saveFailed');
      setModelsState(prev => ({
        ...prev,
        error: msg
      }));
      showError(t('notifications.saveFailed'), msg);
    }
  };

  const clearError = () => {
    setModelsState(prev => ({
      ...prev,
      error: null
    }));
  };

  return {
    // State
    modelsState,
    selectedModelType,
    showSettingsModal,

    // Actions
    openSettings,
    closeSettings,
    saveModelSettings,
    clearError,
    refetchModels: refetch
  };
};